/**
 * Package/space-scoped board→editor reveal.
 *
 * `findDeclaration` is a model-free text scan over the WHOLE VFS — first sorted
 * match wins. In a multi-package workspace (the demo hero, or any VS Code repo
 * with several packages) the same module name can exist in more than one
 * package, so a bare scan jumps the editor to a same-named declaration in the
 * wrong package/space.
 *
 * The board model is already scoped to the active package, and each resolved
 * module carries the `sourceFile` its decl came from. So before scanning text,
 * narrow the VFS to that one file: the reveal can only land where the clicked
 * node was actually declared. Stubs / synthetic modules have no `sourceFile`;
 * for those we fall back to the full VFS (best effort, the prior behaviour).
 */
import type { Vfs } from "@archlang/viewer-core";
import { moduleKey, type ArchitectureModel, type Module } from "@archlang/scene/arch-model";
import { simpleName } from "./sourceMap.js";

/** The `sourceFile` of the module a board id/key names, or undefined. */
export function sourceFileForBoardId(
  model: ArchitectureModel | null,
  id: string,
): string | undefined {
  if (!model) return undefined;
  const name = simpleName(id);
  let nameMatch: string | undefined;
  const walk = (mods: readonly Module[]): string | undefined => {
    for (const m of mods) {
      // Exact key match is authoritative (id / qualified key are unique).
      if (moduleKey(m) === id && m.sourceFile) return m.sourceFile;
      // Remember a leaf-name match as a fallback (the model is already
      // package-scoped, so a name collision here would be intra-package only).
      if (nameMatch === undefined && m.name === name && m.sourceFile) {
        nameMatch = m.sourceFile;
      }
      const child = walk(m.children);
      if (child) return child;
    }
    return undefined;
  };
  return walk(model.modules) ?? nameMatch;
}

/**
 * Narrow `files` to the single source file that declares the module `id` names
 * in the (package-scoped) `model`, so `findDeclaration` cannot escape into
 * another package/space. Falls back to the full VFS when the model can't place
 * the id (e.g. a synthesized stub with no decl).
 */
export function scopedFilesForId(
  files: Vfs,
  model: ArchitectureModel | null,
  id: string,
): Vfs {
  const sourceFile = sourceFileForBoardId(model, id);
  if (sourceFile === undefined) return files;
  const content = files.get(sourceFile);
  if (content === undefined) return files;
  return new Map([[sourceFile, content]]);
}
